export const isPayPalProvider = (provider) => provider === 'paypal' || provider === 'paypal_ppcp'

export const isPaypalPpcpConnectedOnEventType = (eventType) => !!(
  eventType?.is_paypal_ppcp_connected || eventType?.isPaypalPpcpConnected
)

export const isPaypalLegacyConnectedOnEventType = (eventType) => !!(
  eventType?.is_paypal_connected || eventType?.isPaypalConnected
)

/**
 * Which PayPal rows to show: legacy, PPCP, or a single disconnected fallback when neither integration exists.
 */
export const getPaypalFormRowsVisibility = ({
  isPaypalConnected,
  isPaypalPpcpConnected,
  provider,
}) => {
  const showLegacyRow = !!isPaypalConnected || provider === 'paypal'
  const showPpcpRow = !!isPaypalPpcpConnected || provider === 'paypal_ppcp'
  const showDisconnectedPaypalFallback = !showLegacyRow && !showPpcpRow
  return { showLegacyRow, showPpcpRow, showDisconnectedPaypalFallback }
}

/**
 * When saving: form `paypal` → API `paypal`, `paypal_ppcp` → `paypal_ppcp` (explicit processor rows).
 * `none` preserves last non-none provider for PATCH shape compatibility.
 */
export const resolvePaymentProviderForServer = (formProvider, eventType) => {
  const lastPaymentMethod = eventType.payment_method || {}
  const lastProvider = lastPaymentMethod.provider
  const lastValidProvider = lastProvider || 'stripe'

  if (formProvider === 'none') {
    return lastValidProvider
  }

  return formProvider
}

/**
 * Select options for Stripe / PayPal legacy / PayPal PPCP based on row visibility and enable flags.
 */
export const buildProviderOptions = ({
  isStripeEnabled,
  isPaypalLegacyEnabled,
  isPaypalPpcpEnabled,
  paypalRows,
}) => {
  const options = [{
    name: 'Stripe',
    value: 'stripe',
    disabled: !isStripeEnabled,
  }]
  if (paypalRows.showLegacyRow || paypalRows.showDisconnectedPaypalFallback) {
    options.push({
      name: 'PayPal',
      value: 'paypal',
      disabled: !isPaypalLegacyEnabled,
    })
  }
  if (paypalRows.showPpcpRow) {
    options.push({
      name: 'PayPal',
      value: 'paypal_ppcp',
      disabled: !isPaypalPpcpEnabled,
    })
  }
  return options
}

/**
 * Shared enable/disable rules for Stripe vs PayPal (form uses `paypal` for legacy + PPCP).
 * `is*Enabled` flags: whether that processor choice is enabled in the UI (not grayed out).
 * `isProviderDisabled`: whether the `none` provider option ("do not collect") is disabled.
 */
export const computePaymentsEnabledState = ({
  isPaymentsIntegrationAvailable,
  provider,
  paymentMethodEnabled,
  isStripeConnected,
  isPaypalConnected,
  isPaypalPpcpConnected,
  isStripeDisconnected,
  isPaypalDisconnected,
}) => {
  const paypalPpcpActive = !!isPaypalPpcpConnected
  const paypalRows = getPaypalFormRowsVisibility({
    isPaypalConnected,
    isPaypalPpcpConnected,
    provider,
  })

  const paypalLegacyInteractive = isPaypalConnected || (paymentMethodEnabled && provider === 'paypal')
  const paypalPpcpInteractive = paypalPpcpActive || (paymentMethodEnabled && provider === 'paypal_ppcp')

  const paypalLegacyRowVisible = paypalRows.showLegacyRow || paypalRows.showDisconnectedPaypalFallback
  const paypalLegacyEnabledForUi = paypalLegacyRowVisible && paypalLegacyInteractive
  const paypalPpcpRowVisible = paypalRows.showPpcpRow
  const paypalPpcpEnabledForUi = paypalPpcpRowVisible && paypalPpcpInteractive

  if (!isPaymentsIntegrationAvailable) {
    return {
      isProviderDisabled: false,
      isStripeEnabled: paymentMethodEnabled && provider === 'stripe',
      isPaypalLegacyEnabled: paymentMethodEnabled && paypalLegacyEnabledForUi,
      isPaypalPpcpEnabled: paymentMethodEnabled && paypalPpcpEnabledForUi,
    }
  }

  if (
    (paymentMethodEnabled && provider)
    || isStripeConnected
    || isPaypalConnected
    || paypalPpcpActive
  ) {
    const isEnabled = isPaymentsIntegrationAvailable

    return {
      isProviderDisabled: false,
      isStripeEnabled: isEnabled && (isStripeConnected || (paymentMethodEnabled && provider === 'stripe')),
      isPaypalLegacyEnabled: isEnabled && paypalLegacyEnabledForUi,
      isPaypalPpcpEnabled: isEnabled && paypalPpcpEnabledForUi,
    }
  }

  if (isStripeDisconnected || isPaypalDisconnected) {
    return {
      isProviderDisabled: false,
      isStripeEnabled: isStripeConnected,
      isPaypalLegacyEnabled: paypalLegacyEnabledForUi,
      isPaypalPpcpEnabled: paypalPpcpEnabledForUi,
    }
  }

  return {
    isProviderDisabled: !(provider === 'none' && isPaymentsIntegrationAvailable),
    isStripeEnabled: false,
    isPaypalLegacyEnabled: false,
    isPaypalPpcpEnabled: false,
  }
}
